import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { findLast, isNil } from 'lodash';
// Import UI Components
import { Popover, Tooltip, Button, BusyLoader, Empty, Icon } from '@geneui/components';
// Import Constants
import l from 'constants/localizationLabels';
// Import Context
import { useVersionHistoryContext } from './context';
// Import Types
import { Version, VersionHistoryIconWithPopoverProps } from './types';
// Import Styles
import styles from './versionHistory.module.scss';
// Import Services
import HistoryHttpService from 'services/http/history';
// Import Hooks
import { usePartnerDateFormats, useRequest } from 'hooks';
// Import Enums
import { ObjectTypeEnum, ObjectTypeIconsWithTitle } from '.';

export const VersionHistoryIconWithPopover: FC<VersionHistoryIconWithPopoverProps> = ({ objectId, objectTypeId }) => {
    const { t } = useTranslation();
    const [isVersionVisible, setIsVersionVisible] = useState<boolean>(false);
    const { selectedObjectDetails, setSelectedHistoryVersion, setSelectedObjectDetails } = useVersionHistoryContext();
    const [historyVersions, setHistoryVersions] = useState<Version[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { partnerDateTimeFormat } = usePartnerDateFormats();
    
    const [isSuccessfullyLoaded, setIsSuccessfullyLoaded] = useState<boolean>(false);
    const isCampaignOrCJ = [ObjectTypeEnum.Campaign, ObjectTypeEnum.Workflow].includes(objectTypeId);

    const { doGetRequest } = useRequest();

    const { getHistoryList } = useMemo(
        () => ({
            getHistoryList: HistoryHttpService.getHistoryList(),
        }),
        [],
    );

    const isAvailableVersionHistory = useMemo(() => {
        if (isNil(selectedObjectDetails)) {
            return true;
        }

        return selectedObjectDetails.objectId === objectId || selectedObjectDetails.objectTypeId === objectTypeId;
    }, [objectId, objectTypeId, selectedObjectDetails]);

    useEffect(() => {
        if (!isVersionVisible || !objectId || isNil(objectTypeId) || isSuccessfullyLoaded) {
            return;
        }

        setIsLoading(true);
        doGetRequest(getHistoryList.request, {
            queryString: { objectId, objectType: objectTypeId },
            successCallback: (versions: Version[]) => {
                setIsLoading(false);
                setIsSuccessfullyLoaded(true);
                const versionsWithoutSystemUserChanges = versions.filter((version) => version.ModifiedById !== 0);

                const oldestChangeOfSelectedObject = findLast(
                    versionsWithoutSystemUserChanges,
                    (version) => version.ObjectId === +objectId && version.ObjectType === objectTypeId,
                );

                // filter versions to skip changes before the selected object creation
                const filteredVersions = versionsWithoutSystemUserChanges.filter(
                    (version) =>
                        oldestChangeOfSelectedObject && version.PointInTime >= oldestChangeOfSelectedObject.PointInTime,
                );
                setHistoryVersions(filteredVersions);
            },
            errorCallback: () => {
                setIsLoading(false);
            },
        });
    }, [
        doGetRequest,
        getHistoryList.request,
        objectId,
        objectTypeId,
        isVersionVisible,
        historyVersions,
        isSuccessfullyLoaded,
    ]);

    const changeVersionHandler = (version: Version) => {
        setSelectedHistoryVersion(version);
        setSelectedObjectDetails({ objectId: +objectId, objectTypeId: +objectTypeId });
        setIsVersionVisible(false);
    };

    const versionToggleHandler = () => {
        setIsVersionVisible((prev) => !prev);
    };

    return (
        <Popover
            toggleHandler={versionToggleHandler}
            className="used-in-popover"
            isOpen={isVersionVisible}
            behave="toggle"
            align="end"
            extendTargetWidth={false}
            Content={
                <div className={styles.container}>
                    <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="small" loadingText={t(l.Loading)}>
                        {historyVersions.length === 0 ? (
                            <Empty title={t(l.NoData)} subTitle={t(l.NoDataToDisplay)} size="medium" />
                        ) : (
                            <div className={styles.list}>
                                {historyVersions.map((version) => (
                                    <div
                                        key={version.Key}
                                        className={styles.item}
                                        onClick={() => changeVersionHandler(version)}
                                    >
                                        {isCampaignOrCJ && (
                                            <div className={styles.iconContainer}>
                                                <div className={styles.icon}>
                                                    <Tooltip
                                                        text={t(ObjectTypeIconsWithTitle[version.ObjectType]?.title)}
                                                    >
                                                        <Icon
                                                            type={ObjectTypeIconsWithTitle[version.ObjectType]?.icon}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        )}
                                        <div className={styles.details}>
                                            <div className={styles.date}>
                                                {moment.utc(version.PeriodStart).format(partnerDateTimeFormat)}
                                            </div>
                                            <div className={styles.user}>{version.ModifiedByName}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </BusyLoader>
                </div>
            }
        >
            <Tooltip text={t(l.History)}>
                <Button
                    appearance="minimal"
                    color="primary"
                    icon="bc-icon-log-history"
                    flexibility={'default'}
                    disabled={!isAvailableVersionHistory}
                    children={undefined}
                />
            </Tooltip>
        </Popover>
    );
};
